import React from "react";
import { Typography, Card, CardBody } from "@material-tailwind/react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

interface PreMeetingReportViewProps {
  report: {
    preMeetingReceivedInfo?: boolean;
    preMeetingClearGuidance?: boolean;
    preMeetingRemainingDoubts?: boolean;
    preMeetingDoubtsDetails?: string;
    preMeetingDocumentation?: boolean;
    preMeetingRating?: number;
    preMeetingSuggestions?: string;
  };
}

const PreMeetingReportView: React.FC<PreMeetingReportViewProps> = ({ report }) => {
  const getResponseIcon = (value?: boolean) => {
    if (value === true) {
      return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
    } else if (value === false) {
      return <XCircleIcon className="h-5 w-5 text-red-600" />;
    }
    return null;
  };

  const getResponseText = (value?: boolean) => {
    if (value === true) return 'Sim';
    if (value === false) return 'Não';
    return 'Não informado';
  };

  const getResponseColor = (value?: boolean) => {
    if (value === true) return 'text-green-700';
    if (value === false) return 'text-red-700';
    return 'text-gray-500';
  };

  const ReportField = ({ 
    label, 
    value, 
    type = 'text' 
  }: { 
    label: string; 
    value?: string | number | boolean; 
    type?: 'text' | 'boolean' | 'rating';
  }) => {
    let displayValue: React.ReactNode = 'Não informado';
    
    if (type === 'boolean' && typeof value === 'boolean') {
      displayValue = (
        <div className="flex items-center gap-2">
          {getResponseIcon(value)}
          <span className={`font-medium ${getResponseColor(value)}`}>
            {getResponseText(value)}
          </span>
        </div>
      );
    } else if (type === 'boolean' && value === undefined) {
      displayValue = (
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-700">Sim</span>
            </>
          ) : (
            <>
              <XCircleIcon className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-700">Não</span>
            </>
          )}
        </div>
      );
    } else if (type === 'rating' && typeof value === 'number') {
      const stars = '⭐'.repeat(value);
      displayValue = (
        <span className="text-lg font-medium text-yellow-600">
          {stars} ({value}/5)
        </span>
      );
    } else if (value) {
      displayValue = <span className="text-gray-700">{value}</span>;
    }

    return (
      <div className="mb-4">
        <Typography variant="small" className="text-preppoBlue font-medium mb-1 text-xs md:text-sm">
          {label}
        </Typography>
        <div className="text-sm md:text-base">{displayValue}</div>
      </div>
    );
  };

  return (
    <Card className="bg-white shadow-md rounded-xl max-w-3xl w-full">
      <CardBody>
        <Typography
          variant="h5"
          className="mb-4 text-preppoBlue border-b pb-2 text-base md:text-xl"
        >
          Relatório da Reunião Prévia
        </Typography>

        <div className="space-y-1">
          <ReportField
            label="Você recebeu previamente todas as informações necessárias para compreender o processo?"
            value={report.preMeetingReceivedInfo}
            type="boolean"
          />

          <ReportField
            label="As orientações recebidas foram claras e suficientes para sua atuação na audiência?"
            value={report.preMeetingClearGuidance}
            type="boolean"
          />

          <ReportField
            label="Após a reunião, permaneceram dúvidas relevantes sobre o processo ou sobre sua atuação?"
            value={report.preMeetingRemainingDoubts}
            type="boolean"
          />

          {report.preMeetingRemainingDoubts && report.preMeetingDoubtsDetails && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <Typography variant="small" className="text-preppoBlue font-medium mb-1 text-xs md:text-sm">
                Detalhes das dúvidas que permaneceram
              </Typography>
              <Typography className="text-gray-700 text-sm md:text-base whitespace-pre-wrap">
                {report.preMeetingDoubtsDetails}
              </Typography>
            </div>
          )}

          <ReportField
            label="Toda a documentação necessária para a audiência foi disponibilizada?"
            value={report.preMeetingDocumentation}
            type="boolean"
          />

          <ReportField
            label="Avaliação da reunião de alinhamento"
            value={report.preMeetingRating}
            type="rating"
          />

          {report.preMeetingSuggestions && (
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Typography variant="small" className="text-preppoBlue font-medium mb-1 text-xs md:text-sm">
                Sugestões para melhorar a preparação para audiências
              </Typography>
              <Typography className="text-gray-700 text-sm md:text-base whitespace-pre-wrap">
                {report.preMeetingSuggestions}
              </Typography>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default PreMeetingReportView;
